import React from 'react';
import { Head, Link } from '@inertiajs/react';
import AdminLayout from '../../../components/AdminLayout';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Plus, Edit, Eye, Trash2 } from 'lucide-react';

interface BlogPost {
  id: number;
  title: string;
  author: string;
  status: string;
  published_at?: string;
  created_at: string;
  updated_at: string;
}

interface Pagination {
  current_page: number;
  total_pages: number;
  total_count: number;
}

interface Props {
  blog_posts: BlogPost[];
  pagination: Pagination;
}

const BlogPostsIndex: React.FC<Props> = ({ blog_posts, pagination }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'published':
        return 'Publié';
      case 'draft':
        return 'Brouillon';
      case 'pending':
        return 'En attente';
      default:
        return status;
    }
  };

  return (
    <AdminLayout title="Gestion des articles">
      <Head title="Articles - Admin" />
      
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Articles</h1>
            <p className="text-gray-600">
              {pagination.total_count} article{pagination.total_count > 1 ? 's' : ''} au total
            </p>
          </div>
          <Link href="/admin/blog_posts/new">
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Nouvel article
            </Button>
          </Link>
        </div>

        {/* Blog Posts Table */}
        <Card>
          <CardHeader>
            <CardTitle>Liste des articles</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-900">ID</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Titre</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Auteur</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Statut</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Publié le</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-900">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {blog_posts.map((post) => (
                    <tr key={post.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-900">#{post.id}</td>
                      <td className="py-3 px-4 text-sm text-gray-900 font-medium">
                        {post.title}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-500">
                        {post.author}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(post.status)}`}>
                          {getStatusLabel(post.status)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-500">
                        {post.published_at 
                          ? new Date(post.published_at).toLocaleDateString('fr-FR')
                          : 'Non publié'
                        }
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <Link href={`/admin/blog_posts/${post.id}`}>
                            <Button size="sm" variant="ghost">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </Link>
                          <Link href={`/admin/blog_posts/${post.id}/edit`}>
                            <Button size="sm" variant="ghost">
                              <Edit className="w-4 h-4" />
                            </Button>
                          </Link>
                          <Link 
                            href={`/admin/blog_posts/${post.id}`} 
                            method="delete" 
                            as="button"
                            preserveScroll
                            onBefore={() => {
                              if (!confirm('Êtes-vous sûr de vouloir supprimer cet article ?')) {
                                return false;
                              }
                            }}
                          >
                            <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-800">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Page {pagination.current_page} sur {pagination.total_pages}
                </div>
                <div className="flex space-x-2">
                  {pagination.current_page > 1 && (
                    <Link href={`/admin/blog_posts?page=${pagination.current_page - 1}`}>
                      <Button variant="outline" size="sm">
                        Précédent
                      </Button>
                    </Link>
                  )}
                  {pagination.current_page < pagination.total_pages && (
                    <Link href={`/admin/blog_posts?page=${pagination.current_page + 1}`}>
                      <Button variant="outline" size="sm">
                        Suivant
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default BlogPostsIndex; 
